// Basic
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/common-utils/components/ui/collapsible';
import { SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, useSidebar } from '@/common-utils/components/ui/sidebar';
// Common
import ICONS from '@/common-utils/enums/icons';

/**
 * @param root0
 * @param root0.subGroup
 * @param root0.tooltip
 * @param root0.dynamicTextColor
 * @param root0.isLight
 * @param root0.activeButton
 * @param root0.setActiveButton
 * @param root0.clickedButton
 * @param root0.setClickedButton
 * @returns {JSX} SidebarCollapsibleMenu
 */
function SidebarCollapsibleMenu({ subGroup, tooltip, dynamicTextColor, isLight, activeButton, setActiveButton, clickedButton, setClickedButton }) {

    // State
    const [isOpen, setIsOpen] = useState(false);

    // Hooks
    const { open: isOpenSidebar, setOpen: setOpenSidebar, setOpenMobile, isMobile } = useSidebar();

    // Closes others collapsible menus when they don't have focus
    useEffect(() => {
        if (clickedButton !== subGroup.id) {
            setIsOpen(false);
        }
    }, [clickedButton]);

    // Stores active button to prevent css lost when refreshing
    useEffect(() => {
        if (activeButton.includes(subGroup.id)) {
            setIsOpen(true);
        }
    }, [activeButton]);

    // Handles the sidebar and collapsible menu opening/closing when active button is in a collapsible menu
    useEffect(() => {
        if (!isOpenSidebar) {
            setIsOpen(false);
        } else if (activeButton.includes(subGroup.id)) {
            setIsOpen(true);
        }
    }, [isOpenSidebar]);

    // Constants
    const hoverClass = isLight ? 'hover-light' : 'hover-dark';
    const activeClass = activeButton.includes(subGroup.id) && (isLight ? 'is-active-light' : 'is-active-dark');
    const menuButtonClassName = cn(hoverClass, subGroup.id, activeClass, !isOpenSidebar && 'justify-center');

    // Render
    return (
        <Collapsible
            key={subGroup.rootEntity}
            asChild
            defaultOpen={subGroup.isActive}
            className={`group/collapsible menu-group ${activeClass && 'active-button'}`}
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <SidebarMenuItem key={subGroup.rootEntity} data-cy={subGroup.id}>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                        tooltip={tooltip}
                        onClick={() => {
                            setOpenSidebar(true);
                            if (clickedButton !== subGroup.id) {
                                setClickedButton(subGroup.id);
                            }
                        }}
                        className={menuButtonClassName}
                    >
                        <Icon icon={subGroup.items[0].icon} />
                        {isOpenSidebar &&
                            <>
                                <Entity entity={subGroup.rootEntity} style={{ color: dynamicTextColor }} />
                                <Icon icon={ICONS.MORE_INFO} className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
                            </>
                        }
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent className='collapsible-content'>
                    <SidebarMenuSub key={subGroup.rootEntity} style={{ borderColor: dynamicTextColor }}>
                        {subGroup.items?.map((subItem) => (
                            <SidebarMenuSubItem key={subItem.id} data-cy={subItem.id}>
                                <SidebarMenuSubButton asChild>
                                    <Link to={subItem.url} onClick={() => {
                                        if (isMobile) {
                                            setOpenMobile(false);
                                        }
                                        setActiveButton(subItem.id);
                                    }}
                                        className={cn(hoverClass, subItem.id, activeButton === subItem.id && (isLight ? 'is-active-light' : 'is-active-dark'))}
                                    >
                                        <Entity entity={subItem.entity} style={{ color: dynamicTextColor }} />
                                    </Link>
                                </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuItem>
        </Collapsible>
    );
}

SidebarCollapsibleMenu.propTypes = {
    subGroup: PropTypes.object.isRequired,
    tooltip: PropTypes.string.isRequired,
    dynamicTextColor: PropTypes.string.isRequired,
    isLight: PropTypes.bool.isRequired,
    activeButton: PropTypes.string.isRequired,
    setActiveButton: PropTypes.func.isRequired,
    clickedButton: PropTypes.string,
    setClickedButton: PropTypes.func.isRequired
};

export default SidebarCollapsibleMenu;